// src/App.js — Rebaz 餐廳營運台 路由 + 主題
//
// Plan 093 P1：登入 + plan-gated 殼 + 餐廳設定。
// P2 會加訂位路由 (/reservations, /web-booking-settings, public /r/:slug)。
// P3 會加語音路由 (/call-logs, /voice-flows, /outbound-campaigns)。
// P5 會加排隊路由 (/queue, public /q/:slug)。
// nginx 是 SPA catch-all,新增路由不用動 nginx。
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { CompanyProvider } from './contexts/CompanyContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CompanyFormPage from './pages/CompanyFormPage';
import BookingPage from './pages/BookingPage';                 // P2 公開訂位頁 /r/:slug
import BookingManagePage from './pages/BookingManagePage';     // T-015 自助管理 /c/:token
import WebBookingSettingsPage from './pages/WebBookingSettingsPage'; // P2 後台「線上訂位設定」
import RestaurantReservationsPage from './pages/RestaurantReservationsPage'; // P2 inc-2 訂單管理 console（訂位/外帶）
import TablesPage from './pages/TablesPage';                   // 106 桌位管理（自 console 移出）
import MenuSettingsPage from './pages/MenuSettingsPage';       // 106 菜單管理（098 共用）
import CallLogPage from './pages/CallLogPage';                 // P3 通話紀錄
import FaqPage from './pages/FaqPage';                       // T-038 Q&A 管理（自 agent 端移植）
import OutboundCampaignPage from './pages/OutboundCampaignPage';             // P3 外撥活動
import OutboundCampaignDetailPage from './pages/OutboundCampaignDetailPage'; // P3 外撥活動詳情
import VoiceFlowPage from './pages/VoiceFlowPage';             // P3 語音對話腳本

// 登入保護路由 — 共用 toy-app backend 的 JWT。未登入導回 /login。
// 後台分頁標題統一為「Rebaz 餐廳營運台」（公開訂位頁另在各頁自設，見 index.html 預設值）
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('authToken');
  useEffect(() => { document.title = 'Rebaz 餐廳營運台'; }, []);
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

// 主題 — 與 agent.rebaz.ai 同色系 (primary 藍 / secondary LINE 綠)
const theme = createTheme({
  palette: {
    primary: { main: '#145da0' },
    secondary: { main: '#06C755' },
    error: { main: '#8B0000' },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"PingFang TC"',
      '"Microsoft JhengHei"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('authToken') !== null
  );

  // 同步登入狀態（跨分頁 storage 事件 + 同頁 authChange 自訂事件）
  useEffect(() => {
    const checkAuth = () =>
      setIsAuthenticated(localStorage.getItem('authToken') !== null);
    checkAuth();
    window.addEventListener('storage', checkAuth);
    window.addEventListener('authChange', checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', checkAuth);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CompanyProvider>
        <CssBaseline />
        <Routes>
          {/* 公開：登入頁 */}
          <Route path="/login" element={<Login />} />

          {/* 公開：線上訂位頁 (P2 / Plan 090) — 無需登入、不套 Layout，獨立一頁式表單 */}
          <Route path="/r/:slug" element={<BookingPage />} />

          {/* 公開：自助管理訂位 (T-015 cancel-by-token) — 無需登入 */}
          <Route path="/c/:token" element={<BookingManagePage />} />

          {/* 受保護：營運台 */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/profile"
            element={
              <ProtectedRoute>
                <CompanyFormPage />
              </ProtectedRoute>
            }
          />
          {/* 受保護：線上訂位設定 (P2 / Plan 090) — 頁面自帶 Layout，adminOnly 由選單控制 */}
          <Route
            path="/web-booking-settings"
            element={
              <ProtectedRoute>
                <WebBookingSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* 受保護：Q&A 管理 (T-038) — 語音 FAQ 內容維護，頁面自帶 Layout */}
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <FaqPage />
              </ProtectedRoute>
            }
          />
          {/* 受保護：訂單管理 console (P2 inc-2 / Plan 082 + 106 外帶) — 頁面自帶 Layout */}
          <Route
            path="/restaurant/reservations"
            element={
              <ProtectedRoute>
                <RestaurantReservationsPage />
              </ProtectedRoute>
            }
          />
          {/* 受保護：桌位管理（106 §1.2 自 console 移出） */}
          <Route
            path="/restaurant/tables"
            element={
              <ProtectedRoute>
                <TablesPage />
              </ProtectedRoute>
            }
          />
          {/* 受保護：菜單管理（Plan 106 實作、098 共用）— adminOnly 由選單 + 後端擋 */}
          <Route
            path="/menu-settings"
            element={
              <ProtectedRoute>
                <MenuSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/call-logs"
            element={
              <ProtectedRoute>
                <CallLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/outbound-campaigns"
            element={
              <ProtectedRoute>
                <OutboundCampaignPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/outbound-campaigns/:id"
            element={
              <ProtectedRoute>
                <OutboundCampaignDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-flows"
            element={
              <ProtectedRoute>
                <VoiceFlowPage />
              </ProtectedRoute>
            }
          />

          {/* 根路由 + fallback：依登入狀態導向 */}
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
          />
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
          />
        </Routes>
      </CompanyProvider>
    </ThemeProvider>
  );
}

export default App;
